import { useCallback, useMemo, useRef, useState } from "react";
import { ArrowLeft, Search, Plus, ShoppingBag, StickyNote } from "lucide-react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { ConflictRecoveryDialog } from "@/components/captain/ConflictRecoveryDialog";
import { filterMenuItems, menuCategories } from "@/lib/menu";
import { fmtQty, sumQty, lineTotal } from "@/lib/qty";
import { QtyDialog } from "@/components/captain/QtyDialog";
import { inr } from "@/lib/format";
import { EmptyState, LoadingState } from "@/components/ui/StateBoundaries";
import { useCaptainAllTablesState } from "@/hooks/useCaptainOperationalState";
import { useCaptainOrder } from "@/hooks/useCaptainOrder";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { VariantModal } from "@/components/Modals";
import { loadCustomerDraft, clearCustomerDraft } from "@/lib/captainMesh/customerDraft";
import { customerSkipAllowed, draftIdentifiesGuest } from "@/lib/captainMesh/customerSkip";
import { meshTablesPath, meshCustomerPath, meshMenuPath, meshSummaryPath } from "@/lib/captainMesh/routes";
import ItemNoteDialog from "@/components/orders/ItemNoteDialog";
import ItemDetailsDialog from "@/components/menu/ItemDetailsDialog";
import ItemContextMenu from "@/components/menu/ItemContextMenu";
import { useItemPress } from "@/hooks/useItemPress";
import { useNotePresets } from "@/lib/itemNotes";
import SendWaitingButton from "@/components/captain/SendWaitingButton";

export default function CaptainMenu() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const {
    menu, menuHydrated, orderBusy, tables, settings, user, token
  } = usePOS();
  // Half plates are an admin toggle (captain_half_quantity_enabled); an admin
  // signed into a pad may always punch them. UX only — the real gate is
  // order_service's, which refuses fractional quantities server-side.
  const allowHalf =
    user?.role === "admin" ||
    String(settings?.captain_half_quantity_enabled ?? "true").toLowerCase() !== "false";
  
  const { orderForTable, loaded: captureLoaded } = useCaptainAllTablesState();
  
  // Find the table from context
  const table = tables?.find(t => t.id === parseInt(tableId) || t.id === tableId);
  const runningOrder = table ? orderForTable(table.id) : null;
  const isOccupied = !!runningOrder;
  
  const { startOrder, addItem, updateQuantity, removeItem, setNote } = useCaptainOrder(runningOrder?.order_id);
  const { presets: notePresets } = useNotePresets(token);

  // The draft customer from the Customer screen. sessionStorage is the single
  // channel (survives reload and deep links) but React cannot see it change -
  // reading it at render time meant the punch AFTER an order start still saw
  // the cleared draft as truthy and threw the "already being served" dialog at
  // the captain's second item. State mirrors storage; both are cleared together.
  // Route state is the fallback for a sessionStorage that refused the write.
  const routeGuest = useLocation().state?.guest || null;
  const [customerDraft, setCustomerDraft] = useState(() => loadCustomerDraft(tableId) || routeGuest);
  const dropDraft = () => {
    clearCustomerDraft(tableId);
    setCustomerDraft(null);
  };

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [variantPrompt, setVariantPrompt] = useState(null);
  const [conflictPrompt, setConflictPrompt] = useState(null);

  // e2e visibility into the values the punch guard actually reads this render.
  if (typeof window !== "undefined") {
    window.__menuDiag = {
      draft: !!customerDraft,
      occupied: isOccupied,
      agg: runningOrder?.order_id || null,
      conflictOpen: !!conflictPrompt,
    };
  }

  const categories = useMemo(() => menuCategories(menu), [menu]);
  const filtered = useMemo(() => filterMenuItems(menu, category, search), [menu, category, search]);

  const back = () => navigate(meshTablesPath());

  // "removed" is the tombstone the reducer actually writes for a voided line;
  // filtering only "cancelled" kept every voided dish on the bar and the bill.
  const lines = Object.values(runningOrder?.items || {}).filter(
    item => item.status !== "cancelled" && item.status !== "removed"
  );
  // The card tallies, the action bar and the summary all show the CURRENT
  // ROUND — lines no KOT intent has named yet. Once a round is fired it moves
  // to View KOT (long-press) and the catalogue reads fresh: re-opening a table
  // mid-meal is the next round, not a re-showing of food already cooking.
  // `lines` (everything live) still backs the twin/edit logic below.
  const pendingLines = lines.filter(l => !l.kot_intent_event_id);
  // Current punched quantity per menu item, for the [qty] control on each card:
  // the card itself is the running tally (#20) - a waiter reads state off the
  // catalogue, not off toast history.
  const qtyByMenuItem = pendingLines.reduce((acc, l) => {
    const key = Number(l.menu_item_id);
    acc[key] = (acc[key] || 0) + Number(l.quantity || 0);
    return acc;
  }, {});
  const totalQty = sumQty(pendingLines.map(item => item.quantity));
  // Summed in paise: adding rupee floats puts ₹499.99999999 on the bar. This is
  // a running indication only — the bill's arithmetic is the server's.
  const amount =
    Math.round(pendingLines.reduce((paise, item) => paise + lineTotal(item.unit_price, item.quantity) * 100, 0)) / 100;

  // Two fast taps on ADD must not open two orders on one table: the first
  // start is committed to IndexedDB before the second handler runs, but the
  // second handler's render still says "vacant". The ref closes that window.
  const startingRef = useRef(false);
  // The in-flight start, so a rapid second punch can WAIT for it and then land
  // on the real aggregate — instead of being dropped, or worse, racing a
  // second ORDER_STARTED onto a table the first one is about to claim (the
  // server then refuses it and the captain is told their own table is taken).
  const startPromiseRef = useRef(null);
  // Set once this screen has opened the table's order. dropDraft() clears the
  // guest a moment before the new order shows up in the local live query; the
  // Customer-step guard below must not read that gap as "a new table with no
  // guest" and bounce the captain mid-punch.
  const startedHereRef = useRef(false);

  const performAtomicStart = async (item, variant, quantity) => {
    startingRef.current = true;
    try {
      const payload = {
        table_id: table.id,
        guest_count: table.covers || 2,
        order_type: "dine_in", staff_name: user?.name,
        customer: customerDraft ? {
          id: customerDraft.id || null,
          name: customerDraft.name || null,
          phone: customerDraft.phone || null,
          dob: customerDraft.dob || null,
          anniversary: customerDraft.anniversary || null
        } : null,
        first_items: [{
          menu_item_id: Number(item.id),
          item_name: variant ? `${item.name} · ${variant.name}` : item.name,
          unit_price: variant ? variant.price : item.price,
          quantity: quantity,
          notes: null,
          modifier_ids: [],
          variation_id: variant ? Number(variant.id) : null
        }]
      };
      
      const started = await startOrder(payload);
      if (!started) return false;
      startedHereRef.current = true;

      // A taken table does not arrive as a thrown 409 — nothing on the mesh path
      // throws one. Reconciliation answers the ORDER_STARTED with a conflict and
      // syncService parks it, so that list is the only place the refusal exists.
      const { syncService } = await import("@/lib/captainMesh/application/syncService");
      // A busy table is no longer a refusal (the server joins the running
      // order), so a conflict here is a real "no" — a section this captain may
      // not serve, an identity the server cannot verify. CaptainLayout names it.
      if (syncService.conflicts.some(c => c.aggregate_id === started.aggregate_id)) {
        console.warn("[CaptainMenu] start refused by reconcile", syncService.conflicts);
        return false;
      }

      dropDraft();
      return true;
    } catch (e) {
          console.error("[CaptainMenu] start failed", e);
          // Surface more specific error details
          let msg = "Failed to start order";
          if (e?.response?.data?.detail) {
            const d = e.response.data.detail;
            if (d?.code === "TABLE_ALREADY_OCCUPIED") {
              msg = `Table is already occupied by ${d.staff_name || "another captain"}`;
            } else if (typeof d === "string") {
              msg = d;
            } else if (d?.message) {
              msg = d.message;
            }
          } else if (e?.message) {
            msg = e.message;
          }
          toast.error(msg);
          return false;
        } finally {
      startingRef.current = false;
    }
  };

  const punch = async (item, variant, quantity) => {
    // 1. Occupied + draft = SOMEONE ELSE claimed the table while we were still
    // collecting the guest - warn before merging (§13). But not while OUR OWN
    // start is in flight: the first item materialises (and flips isOccupied)
    // before the awaited reconcile returns and clears the draft, so for a few
    // hundred milliseconds this state describes our own order. Firing the
    // dialog then told the captain their own table was taken - the walkthrough
    // caught it as a race the desktop suite never hit.
    //
    // Any captain may serve any table (owner, 2026-10-23): the punch simply
    // joins the order already running there. A modal asking "use existing
    // order?" on every busy table was a click staff had to make dozens of times
    // a night. The draft guest is dropped rather than overwriting the guest
    // already on the order; Edit Customer still changes it deliberately.
    if (isOccupied && customerDraft && !conflictPrompt && !startingRef.current) {
      dropDraft();
    }

    // 2. A start already in flight: wait for it, then punch onto whatever
    // aggregate now holds the table. Every path out of the old behaviour was
    // wrong - dropping the tap, double-starting, or dialog-ing the captain.
    if (startingRef.current && startPromiseRef.current) {
      await startPromiseRef.current.catch(() => {});
      const { findLiveAggregateForTable } = await import("@/lib/captainMesh/db");
      const winner = await findLiveAggregateForTable(table.id);
      if (winner) {
        // Same consolidation rule as the settled path: an identical unsent
        // line absorbs the punch - the queued path bypassing it was how
        // "Sprite, Sprite" still printed as two lines.
        const winnerTwin = Object.values(winner.state?.items || {}).find(
          l =>
            l.status === "active" &&
            Number(l.menu_item_id) === Number(item.id) &&
            (l.variation_id || null) === (variant ? Number(variant.id) : null) &&
            !(l.notes || "").trim() &&
            !l.kot_intent_event_id
        );
        const result = winnerTwin
          ? await updateQuantity(winnerTwin.line_id, quantity, winner.aggregate_id)
          : await addItem(item, variant, quantity, winner.aggregate_id);
        if (result) {
          const name = variant ? `${item.name} · ${variant.name}` : item.name;
          toast.success(`${quantity === 1 ? name : `${fmtQty(quantity)} × ${name}`} added`);
        }
        return;
      }
      // The start failed; fall through and start fresh below.
    }

    if (!isOccupied) {
      // 2b. Perform Atomic Order Start
      startPromiseRef.current = performAtomicStart(item, variant, quantity);
      const success = await startPromiseRef.current;
      startPromiseRef.current = null;
      if (success) {
        const name = variant ? `${item.name} · ${variant.name}` : item.name;
        toast.success(`${quantity === 1 ? name : `${fmtQty(quantity)} × ${name}`} added`);
      } else if (!conflictPrompt) {
        // A falsy start that did NOT throw and is not a table conflict already
        // toasted by performAtomicStart. Without this the tap is completely
        // silent: nothing on screen, nothing in the order, and a captain taps
        // the same dish three more times believing the tablet missed it.
        toast.error("Could not add that item — nothing was punched. Try again.");
      }
      return;
    }

    // 3. Existing order. An identical UNSENT line absorbs the punch as +qty -
    // "Sprite, Sprite" must be one line at quantity 2, on the screen, on the
    // KOT and on the bill (consolidation lives here at the order layer, not in
    // display CSS). Different variant or notes stay separate lines; a line the
    // kitchen already has stays untouched and the punch becomes a new line.
    const twin = lines.find(
      l =>
        Number(l.menu_item_id) === Number(item.id) &&
        (l.variation_id || null) === (variant ? Number(variant.id) : null) &&
        !(l.notes || "").trim() &&
        !l.kot_intent_event_id
    );
    let result;
    try {
      result = twin
        ? await updateQuantity(twin.line_id, quantity)
        : await addItem(item, variant, quantity);
    } catch (err) {
      // A failed punch used to be an unhandled rejection: nothing on screen, so the
      // captain tapped ADD again and again (2026-10-14). Say it, by name.
      toast.error(`Could not add ${item.name} — nothing was punched: ${err?.message || "unknown error"}`);
      return;
    }
    if (!result) return;

    const name = variant ? `${item.name} · ${variant.name}` : item.name;
    toast.success(`${quantity === 1 ? name : `${fmtQty(quantity)} × ${name}`} added`);
  };

  const resolveConflict = async (useExisting) => {
    const { item, variant, quantity } = conflictPrompt;
    setConflictPrompt(null);
    if (!useExisting) return; // Cancel

    // Using the existing order discards the draft rather than merging two
    // customer identities onto one bill (§13). The guest is re-attached from the
    // Customer screen if the captain still wants them on this order.
    dropDraft();

    // Re-resolve the target from Dexie, not from this closure: our refused
    // aggregate was just quarantined, and the hook's addItem is bound to it —
    // punching through the binding would feed the order the server keeps
    // refusing. The winner is whatever live aggregate now holds the table.
    const { findLiveAggregateForTable } = await import("@/lib/captainMesh/db");
    const winner = await findLiveAggregateForTable(table.id);
    if (!winner) {
      toast.error("The other terminal's order hasn't reached this device yet — reopen the table in a moment");
      return;
    }

    const result = await addItem(item, variant, quantity, winner.aggregate_id);
    if (!result) return;

    const name = variant ? `${item.name} · ${variant.name}` : item.name;
    toast.success(`${quantity === 1 ? name : `${fmtQty(quantity)} × ${name}`} added`);
  };

  const add = async (item, quantity = 1) => {
    if (item.variants?.length) {
      setVariantPrompt({ item, quantity });
      return;
    }
    await punch(item, null, quantity);
  };

  const chooseVariant = async (variant) => {
    const chosen = variantPrompt;
    setVariantPrompt(null);
    if (!chosen) return;
    await punch(chosen.item, variant, chosen.quantity);
  };

  const [qtyPrompt, setQtyPrompt] = useState(null);

  // Special note (owner, 2026-10-20): tapping a dish already in this round opens
  // its note — "extra spicy" on the dal, not the roti. Long-press (or right-click
  // with a mouse) opens the dish's details. Both are one dialog at a time.
  const [noteFor, setNoteFor] = useState(null);       // a pending line
  const [detailsFor, setDetailsFor] = useState(null); // a menu item
  const [contextFor, setContextFor] = useState(null); // { item, at }
  const press = useItemPress({
    onLongPress: item => setDetailsFor(item),
    onContextMenu: (item, at) => setContextFor({ item, at }),
  });
  const closeContext = useCallback(() => setContextFor(null), []);
  // The newest un-fired line of the dish is the one a note goes on: a noted line
  // never absorbs later punches, so a fresh ADD after a note is its own line.
  const pendingLineFor = item =>
    pendingLines.filter(l => Number(l.menu_item_id) === Number(item.id)).slice(-1)[0] || null;
  const openNote = item => {
    const line = pendingLineFor(item);
    if (!line) {
      toast.info(`Add ${item.name} first, then tap it to write a note`);
      return;
    }
    setNoteFor(line);
  };
  const saveNote = async note => {
    const line = noteFor;
    await setNote(line.line_id, note);
    toast.success(note ? `Note saved for ${line.item_name}` : `Note removed from ${line.item_name}`);
    return true;
  };

  // Tapping the [qty] control sets the item's TOTAL quantity. ADD stays +1; any
  // other figure - 0.5, 1.5, 2.5 - is set here, replacing the old permanent
  // half-plate button that spent a column of every card on one niche action.
  const applyQty = async (item, newQty) => {
    setQtyPrompt(null);
    const current = qtyByMenuItem[Number(item.id)] || 0;
    const delta = Math.round((newQty - current) * 100) / 100;
    if (delta === 0) return;

    // Zero means "take it off entirely" — remove the round's lines for this
    // item rather than leaving a ghost line at quantity 0.
    if (newQty === 0) {
      const removable = lines.filter(
        l => Number(l.menu_item_id) === Number(item.id) && !l.kot_intent_event_id
      );
      for (const line of removable) await removeItem(line.line_id);
      if (removable.length) toast.success(`${item.name} removed`);
      return;
    }

    if (!current) {
      await punch(item, null, newQty);
      return;
    }
    const itemLines = lines.filter(l => Number(l.menu_item_id) === Number(item.id));
    const editable = itemLines.find(l => !l.kot_intent_event_id);
    if (delta > 0) {
      if (editable) {
        await updateQuantity(editable.line_id, delta);
      } else {
        // Every existing line is already named by a KOT intent; the kitchen has
        // (or will get) those. More food is a NEW line, not a rewrite of one.
        await addItem(item, null, delta);
      }
      toast.success(`${item.name}: ${fmtQty(newQty)}`);
      return;
    }
    if (!editable || Number(editable.quantity) + delta < 0) {
      toast.error("Sent quantities can only be reduced from the order summary");
      return;
    }
    await updateQuantity(editable.line_id, delta);
    toast.success(`${item.name}: ${fmtQty(newQty)}`);
  };

  // captain_customer_skip_allowed off: a NEW table may not be punched without
  // the guest's name and mobile. Checked here too, not only by hiding Skip, so
  // a reload, a back-swipe or a typed URL cannot walk around the Customer step.
  // Never applied to a table that already holds an order (this pad's or the
  // server's), and never before the local captures have been read.
  const mustIdentify =
    !customerSkipAllowed(settings, user) &&
    captureLoaded &&
    !!table &&
    !runningOrder &&
    !startingRef.current &&
    !startedHereRef.current &&
    !["occupied", "billing"].includes(table.status) &&
    !draftIdentifiesGuest(customerDraft);
  if (mustIdentify) {
    return <Navigate to={meshCustomerPath(tableId)} replace />;
  }

  return (
    <div className="flex flex-col h-full bg-background relative animate-slide-up" data-testid="captain-menu-screen">
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md sticky top-0 z-20 shrink-0 gap-3">
        <div className="flex items-center gap-2">
          <button 
            className="p-3 -ml-2 rounded-full hover:bg-primary-foreground/20 transition-colors" 
            aria-label="Back to tables" 
            onClick={back}
          >
            <ArrowLeft className="w-6 h-6"/>
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase truncate max-w-[60vw]">
              {runningOrder
                ? `ORDER${runningOrder.order_number ? ` #${runningOrder.order_number}` : ""}${runningOrder.customer?.name ? ` · ${runningOrder.customer.name}` : ""}`
                : 'DRAFT'}
            </small>
            <h2 className="text-title font-bold leading-tight">{table?.name}</h2>
          </div>
        </div>
        <NetworkIndicator />
      </header>

      {/* SEARCH + CATEGORY CHIPS (Fixed under header) */}
      <div className="shrink-0 bg-background/95 backdrop-blur border-b z-10">
        <div className="p-3 pb-snug">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              className="w-full h-12 pl-10 pr-4 rounded-xl bg-card border shadow-sm text-body font-medium focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder="Search menu…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Chips, not a rail: the vertical column cost 96px of every row on a
            348-item menu, and category is a filter the captain touches once. */}
        <div className="flex gap-snug overflow-x-auto scrollbar-hide px-3 pb-3">
          {categories.map(cat => (
            <button
              className={`h-11 px-4 flex-none rounded-full text-meta font-bold whitespace-nowrap transition-colors ${category === cat ? "bg-primary text-primary-foreground" : "bg-card border text-muted-foreground"}`}
              onClick={() => setCategory(cat)}
              data-testid={`category-chip-${cat}`}
              key={cat}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* MENU LIST */}
      <div className="flex-1 min-h-0 overflow-y-auto p-snug space-y-snug pb-section bg-muted/10" data-testid="captain-menu-list">
        {filtered.map((item, i) => (
          <div
            className="flex items-center gap-3 p-3 bg-card border rounded-xl shadow-sm animate-pop select-none"
            style={{ animationDelay: `${i * 15}ms`, WebkitTouchCallout: "none" }}
            key={item.id}
            data-testid={`menu-card-${item.name}`}
            {...press.bind(item)}
          >
            <span className={`w-3 h-3 border-[1.5px] rounded-sm flex items-center justify-center shrink-0 ${item.veg ? "border-emerald-600" : "border-rose-600"}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${item.veg ? "bg-emerald-600" : "bg-rose-600"}`}></span>
            </span>
            <div
              className="flex-1 min-w-0 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => openNote(item)}
              onKeyDown={e => { if (e.key === "Enter") openNote(item); }}
              aria-label={`Note for ${item.name}`}
              data-testid={`menu-card-body-${item.name}`}
            >
              {/* Wraps instead of truncating: the cut-off end is often what tells
                  two dishes apart ("… (2 Pcs)" / "… (4 Pcs)") on a 360px phone. */}
              <strong className="block text-body font-semibold leading-snug text-foreground mb-1 [overflow-wrap:anywhere]">{item.name}</strong>
              {/* The price is what the captain reads aloud: foreground and body size,
                  not micro capitals in the selection blue. The category is context. */}
              <small className="flex items-baseline gap-1.5 min-w-0 text-meta text-muted-foreground">
                <span className="text-body font-semibold text-foreground tabular-nums shrink-0">{inr(item.price)}</span>
                <span className="truncate">{item.category}{item.variants?.length ? " · varies" : ""}</span>
              </small>
              {pendingLineFor(item)?.notes && (
                <span className="flex items-center gap-1 text-meta text-warning truncate mt-0.5" data-testid={`menu-card-note-${item.name}`}>
                  <StickyNote className="w-3.5 h-3.5 shrink-0" />{pendingLineFor(item).notes}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                className={`w-11 h-11 rounded-lg font-bold text-body tabular-nums active:scale-90 transition-transform disabled:opacity-50 ${qtyByMenuItem[Number(item.id)] ? "bg-primary/10 text-primary border border-primary/40" : "bg-muted text-muted-foreground"}`}
                onClick={() => {
                  if (item.variants?.length) {
                    toast.info("This item has variants — use ADD to pick one");
                    return;
                  }
                  setQtyPrompt(item);
                }}
                disabled={orderBusy}
                aria-label={`Set quantity for ${item.name}`}
                data-testid={`qty-btn-${item.name}`}
              >{qtyByMenuItem[Number(item.id)]
                ? fmtQty(qtyByMenuItem[Number(item.id)])
                // "–" read as a minus button beside ADD; this control SETS a quantity.
                : <span className="text-micro font-semibold">Qty</span>}</button>
              <button
                data-testid={`add-btn-${item.name}`}
                className="h-11 px-4 rounded-lg bg-action text-action-foreground font-bold text-body active:scale-90 transition-transform disabled:opacity-50 flex items-center gap-1"
                onClick={() => add(item)}
                disabled={orderBusy}
              ><Plus className="w-4 h-4 -ml-1"/> ADD</button>
            </div>
          </div>
        ))}

        {!filtered.length && (
          !menuHydrated
            ? <LoadingState message="Reading the menu…" />
            : !menu?.length
              ? <EmptyState title="Menu not loaded" message="Check the connection and reload." />
              : <EmptyState title="No items found" message="No items match that search." />
        )}
      </div>

      {/* ACTION BAR — running count and one explicit way out. Deliberately not a
          summary: no lines, no tax, no breakdown. Those are the Summary screen's,
          and the amount here is an indication, never the bill. */}
      {isOccupied && pendingLines.length === 0 && (
        <footer className="pb-safe px-4 py-2 bg-card border-t sticky bottom-0 z-30 shrink-0 empty:hidden">
          <SendWaitingButton tableId={table?.id} show className="w-full" />
        </footer>
      )}
      {isOccupied && pendingLines.length > 0 && (
        <footer className="min-h-16 pb-safe flex items-center justify-between px-4 gap-3 bg-card border-t shadow-[0_-4px_12px_rgba(0,0,0,0.05)] sticky bottom-0 z-30 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <ShoppingBag className="w-5 h-5"/>
            </div>
            <b className="text-body font-bold tabular-nums truncate">{fmtQty(totalQty)} items · {inr(amount)}</b>
          </div>
          <button
            data-testid="view-order-button"
            className="h-11 px-5 shrink-0 rounded-lg bg-primary text-primary-foreground font-bold text-body active:scale-90 transition-transform"
            onClick={() => navigate(meshSummaryPath(tableId))}
          >View Order</button>
        </footer>
      )}

      {qtyPrompt && (
        <QtyDialog
          itemName={qtyPrompt.name}
          initial={qtyByMenuItem[Number(qtyPrompt.id)] || 1}
          allowHalf={allowHalf}
          onDone={q => applyQty(qtyPrompt, q)}
          onClose={() => setQtyPrompt(null)}
        />
      )}

      {noteFor && (
        <ItemNoteDialog
          key={noteFor.line_id}
          itemName={noteFor.item_name}
          initial={noteFor.notes || ""}
          presets={notePresets}
          onSave={saveNote}
          onClose={() => setNoteFor(null)}
        />
      )}

      {detailsFor && (
        <ItemDetailsDialog item={detailsFor} variant="sheet" onClose={() => setDetailsFor(null)} />
      )}

      {contextFor && (
        <ItemContextMenu
          at={contextFor.at}
          onShowDetails={() => setDetailsFor(contextFor.item)}
          onClose={closeContext}
        />
      )}

      {variantPrompt && (
        <VariantModal
          item={variantPrompt.item}
          onSelect={chooseVariant}
          onClose={() => setVariantPrompt(null)}
        />
      )}

      
      <ConflictRecoveryDialog runningOrder={runningOrder} table={table} meshTablesPath={meshTablesPath} addItem={addItem} />

      {conflictPrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in" data-testid="table-conflict-dialog">
          <div className="bg-card w-full max-w-sm rounded-2xl shadow-xl overflow-hidden border">
            <div className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-3 leading-tight">{`${table?.name} is already being served`}</h3>
              <p className="text-body text-muted-foreground">
                Another CaptainPAD (served by {runningOrder?.staff_name || "someone else"}) has this table open. 
              </p>
            </div>
            <div className="flex flex-col border-t bg-muted/10 divide-y">
              <button
                data-testid="use-existing-order-button"
                className="w-full text-left p-4 hover:bg-muted/30 transition-colors"
                onClick={() => resolveConflict(true)}
              >
                <div className="font-bold text-body text-primary">Use existing order</div>
                <div className="text-micro text-muted-foreground mt-1">Add my item to that order. My guest details are discarded.</div>
              </button>
              <button
                className="w-full text-left p-4 hover:bg-muted/30 transition-colors"
                onClick={() => resolveConflict(false)}
              >
                <div className="font-bold text-body text-foreground">Cancel</div>
                <div className="text-micro text-muted-foreground mt-1">Keep my guest details and do not add the item.</div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
